import { _ as __decorate, a as __metadata } from '../tslib.es6-Cv888lzs.js';
import { queryAssignedElements, property, customElement } from 'lit/decorators.js';
import { ItRadioGroup } from '@italia/radio';
import { registerTranslation } from '@italia/i18n';
import { css } from 'lit';

const translation = {
    $code: 'it',
    $name: 'Italiano',
    $dir: 'ltr',
    noRating: 'Nessuna valutazione',
    ratingLabelSingle: (rating, max) => `${rating} stella su ${max}`,
    ratingLabelPlural: (rating, max) => `${rating} stelle su ${max}`,
};

var styles = css`*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.rating {
  --bsi-rating-font-size: var(--bsi-label-font-size-s);
  --bsi-rating-font-weight: var(--bsi-font-weight-solid);
  --bsi-rating-icon-fill: var(--bsi-color-background-secondary-light);
  --bsi-rating-line-height: var(--bsi-label-leading);
  --bsi-rating-text: var(--bsi-color-text-secondary);
}

/* stylelint-disable-next-line no-duplicate-selectors */
.rating {
  border: none;
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;
  align-items: center;
}
.rating > input {
  position: absolute;
  left: -9999px;
}
.rating > label {
  margin: 0;
  padding-right: 2px;
}
.rating > label:hover {
  cursor: pointer;
}
.rating > label:hover svg {
  transform: scale(1.2);
}
.rating > label svg {
  fill: var(--bsi-rating-icon-fill);
  position: relative;
  transform-origin: center center;
  transition: transform var(--bsi-transition-instant);
}
.rating > legend {
  color: var(--bsi-rating-text);
  font-size: var(--bsi-rating-font-size);
  float: right;
  width: auto;
  margin-left: var(--bsi-spacing-xxs);
  font-weight: var(--bsi-rating-font-weight);
  line-height: var(--bsi-rating-line-height);
}
.rating.rating-read-only > input, .rating.rating-read-only > label {
  pointer-events: none;
}

.rating > input:checked ~ label,
.rating:not(:checked) > label:hover,
.rating:not(:checked) > label:hover ~ label {
  --bsi-rating-icon-fill: var(--bsi-icon-primary);
}

.rating > input:checked + label:hover svg,
.rating > input:checked ~ label:hover svg,
.rating > label:hover ~ input:checked ~ label svg,
.rating > input:checked ~ label:hover ~ label svg {
  --bsi-rating-icon-fill: var(--bsi-icon-primary);
}

:host {
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  align-items: center;
  --bsi-rating-icon-fill: var(--bsi-icon-disabled);
  --bsi-rating-text: var(--bsi-color-text-secondary);
  --bsi-rating-font-size: var(--bsi-label-font-size-s);
  --bsi-rating-line-height: var(--bsi-label-leading);
  --bsi-rating-font-weight: var(--bsi-font-weight-solid);
}

div.it-radio-group {
  display: flex;
}

::slotted([slot=label]) {
  width: auto;
  margin-left: var(--bsi-spacing-xxs);
  color: var(--bsi-rating-text);
  float: right;
  font-size: var(--bsi-rating-font-size);
  font-weight: var(--bsi-rating-font-weight);
  line-height: var(--bsi-rating-line-height);
}`;

registerTranslation(translation);
/**
 * Rating component - extends it-radio-group for star rating
 *
 * @element it-rating
 *
 * @slot - Contains the `<it-rating-item>` elements
 * @slot label - The label/legend text for the rating
 *
 * @fires {CustomEvent} change - Emitted when the rating value changes
 */
let ItRating = class ItRating extends ItRadioGroup {
    constructor() {
        super(...arguments);
        /**
         * Maximum number of stars (to calculate "X out of Y")
         */
        this.maxElements = 5;
        /**
         * Whether the rating is read-only (non-interactive)
         */
        this.readOnly = false;
        /**
         * The icon name to use for the rating item
         * @default 'it-star-outline'
         */
        this.icon = 'it-star-outline';
    }
    /**
     * Sync read-only state to all rating items
     */
    _syncReadOnlyToItems() {
        const items = this.querySelectorAll('it-rating-item');
        items.forEach((item) => {
            if (this.readOnly) {
                item.setAttribute('disabled', '');
            }
            else {
                item.removeAttribute('disabled');
            }
        });
    }
    /**
     * Get the localized rating announcement for screen readers
     */
    _getRatingAnnouncement() {
        const currentValue = parseInt(this.value || '0', 10);
        if (!currentValue || currentValue === 0) {
            return this.localize.term('noRating');
        }
        // Use singular or plural form based on rating value
        const termKey = currentValue === 1 ? 'ratingLabelSingle' : 'ratingLabelPlural';
        // Pass parameters directly to the translation function
        return this.localize.term(termKey, currentValue, this.maxElements);
    }
    /**
     * Override updated to refresh the rating items and announcement when value changes
     */
    updated(changedProperties) {
        super.updated(changedProperties);
        // Sync disabled state to all rating items when readOnly changes
        if (changedProperties.has('readOnly')) {
            this._syncReadOnlyToItems();
        }
        // Update aria-readonly attribute
        if (this.readOnly) {
            this.setAttribute('aria-readonly', 'true');
        }
        else {
            this.removeAttribute('aria-readonly');
        }
        // When value changes, force re-render of all rating items AND update their display
        if (changedProperties.has('value')) {
            this._updateRadiosState();
            this._updateRatingItemsDisplay();
            this._ensureAnnouncement();
        }
        if (changedProperties.has('icon')) {
            this._updateRatingItemsDisplay();
        }
        // When maxElements changes, update the announcement
        if (changedProperties.has('maxElements')) {
            this._ensureAnnouncement();
        }
    }
    /**
     * Force re-render of all rating items to update star colors
     * This ensures star colors reflect the current value
     */
    _updateRatingItemsDisplay() {
        const items = this.querySelectorAll('it-rating-item');
        items.forEach((item) => {
            item.requestUpdate();
        });
    }
    /**
     * Create or update the visually-hidden announcement element
     * Following BSI pattern: announcement placed directly in the label slot
     */
    _ensureAnnouncement() {
        // Find the label slot elements (assigned nodes)
        const labelSlot = this.shadowRoot?.querySelector('slot[name="label"]');
        if (!labelSlot)
            return;
        // Get the first element in the label slot (the legend/label container)
        const labelElements = labelSlot.assignedElements();
        if (labelElements.length === 0)
            return;
        const labelContainer = labelElements[0];
        // Look for existing announcement span
        let announcement = labelContainer.querySelector('.rating-announcement');
        if (!announcement) {
            // Create the announcement element if it doesn't exist
            announcement = document.createElement('span');
            announcement.className = 'visually-hidden rating-announcement';
            // Append to the label container
            labelContainer.appendChild(announcement);
        }
        // Update the text content
        announcement.textContent = ` ${this._getRatingAnnouncement()}`;
    }
};
ItRating.styles = styles;
__decorate([
    queryAssignedElements(),
    __metadata("design:type", Array)
], ItRating.prototype, "_radios", void 0);
__decorate([
    property({ type: Number, attribute: 'max-elements', reflect: true }),
    __metadata("design:type", Object)
], ItRating.prototype, "maxElements", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'read-only' }),
    __metadata("design:type", Object)
], ItRating.prototype, "readOnly", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItRating.prototype, "icon", void 0);
ItRating = __decorate([
    customElement('it-rating')
], ItRating);

export { ItRating };
//# sourceMappingURL=it-rating.js.map
